import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { 
  Calendar, 
  Users, 
  AlertCircle,
  Settings,
  User,
  Mail,
  Phone,
  MapPin,
  Globe,
  Wrench
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { DealerUser, Dealer } from '../types';
import apiService from '../services/api';
import toast from 'react-hot-toast';

const PageContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 20px;
`;

const PageHeader = styled.div`
  margin-bottom: 32px;
  
  .welcome-text {
    font-size: 2rem;
    font-weight: 700;
    color: #1f2937;
    margin-bottom: 8px;
  }
  
  .subtitle {
    color: #6b7280;
    font-size: 1.125rem;
  }
`;

const DashboardGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 24px;
  margin-bottom: 32px;
`;

const StatsCard = styled.div`
  background: white;
  border-radius: 12px;
  padding: 24px;
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1);
  border: 1px solid #e5e7eb;
  
  .stat-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 16px;
    
    .stat-title {
      font-size: 0.875rem;
      font-weight: 600;
      color: #6b7280;
      text-transform: uppercase;
      letter-spacing: 0.05em;
    }
    
    .stat-icon {
      width: 40px;
      height: 40px;
      border-radius: 8px;
      display: flex;
      align-items: center;
      justify-content: center;
      
      &.primary {
        background: linear-gradient(135deg, #ed2024 0%, #a01519 100%);
        color: white;
      }
      
      &.success {
        background: linear-gradient(135deg, #10b981 0%, #059669 100%);
        color: white;
      }
      
      &.warning {
        background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%);
        color: white;
      }
      
      &.info {
        background: linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%);
        color: white;
      }
      
      svg {
        width: 20px;
        height: 20px;
      }
    }
  }
  
  .stat-value {
    font-size: 2rem;
    font-weight: 700;
    color: #1f2937;
    margin-bottom: 4px;
  }
  
  .stat-description {
    font-size: 0.875rem;
    color: #6b7280;
  }
`;

const InfoCard = styled.div`
  background: white;
  border-radius: 12px;
  padding: 24px;
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1);
  border: 1px solid #e5e7eb;
  margin-bottom: 24px;
  
  .card-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 20px;
    
    h2 {
      margin: 0;
      font-size: 1.25rem;
      font-weight: 600;
      color: #1f2937;
    }
  }
`;

const DealerInfo = styled.div`
  .dealer-name {
    font-size: 1.5rem;
    font-weight: 700;
    color: #1f2937;
    margin-bottom: 16px;
  }
  
  .info-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: 16px;
  }
  
  .info-item {
    display: flex;
    align-items: center;
    gap: 8px;
    color: #6b7280;
    
    svg {
      width: 16px;
      height: 16px;
      color: #9ca3af;
    }
  }
`;

const ActionButton = styled.button<{ variant?: 'primary' | 'secondary' }>`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 20px;
  border: ${props => props.variant === 'primary' ? 'none' : '1px solid #d1d5db'};
  background: ${props => props.variant === 'primary' ? 'linear-gradient(135deg, #ed2024 0%, #a01519 100%)' : 'white'};
  color: ${props => props.variant === 'primary' ? 'white' : '#374151'};
  border-radius: 8px;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover:not(:disabled) {
    ${props => props.variant === 'primary' 
      ? 'transform: translateY(-1px); box-shadow: 0 4px 12px rgba(102, 126, 234, 0.4);'
      : 'background: #f3f4f6; border-color: #9ca3af;'
    }
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
  
  svg {
    width: 16px;
    height: 16px;
  }
`;

const LoadingSpinner = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 60px;
  
  .spinner {
    width: 32px;
    height: 32px;
    border: 3px solid #e5e7eb;
    border-top: 3px solid #ed2024;
    border-radius: 50%;
    animation: spin 1s linear infinite;
  }
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const DealerDashboard: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [dealer, setDealer] = useState<Dealer | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const dealerUser = user as DealerUser;

  useEffect(() => {
    if (dealerUser?.dealerId) {
      loadDealerInfo();
    }
  }, [dealerUser?.dealerId]);

  const loadDealerInfo = async () => {
    if (!dealerUser?.dealerId) return;
    
    try {
      setIsLoading(true);
      const response = await apiService.getDealer(dealerUser.dealerId);
      
      if (response.success && response.data) {
        setDealer(response.data);
      } else {
        toast.error('Failed to load dealer information');
      }
    } catch (error) {
      console.error('Error loading dealer info:', error);
      toast.error('Failed to load dealer information');
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return (
      <PageContainer>
        <LoadingSpinner>
          <div className="spinner" />
        </LoadingSpinner>
      </PageContainer>
    );
  }

  if (!dealer) {
    return (
      <PageContainer>
        <div style={{ textAlign: 'center', padding: '60px', color: '#6b7280' }}>
          Failed to load dealer information.
        </div>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <PageHeader>
        <div className="welcome-text">
          Welcome back, {dealerUser.firstName}!
        </div>
        <div className="subtitle">
          {dealer.name} Dashboard
        </div>
      </PageHeader>

      <DashboardGrid>
        <StatsCard>
          <div className="stat-header">
            <div className="stat-title">Today's Appointments</div>
            <div className="stat-icon primary">
              <Calendar />
            </div>
          </div>
          <div className="stat-value">--</div>
          <div className="stat-description">Coming soon with calendar integration</div>
        </StatsCard>

        <StatsCard>
          <div className="stat-header">
            <div className="stat-title">Active Users</div>
            <div className="stat-icon info">
              <Users />
            </div>
          </div>
          <div className="stat-value">
            {dealerUser.role === 'dealer_admin' ? '1+' : '1'}
          </div>
          <div className="stat-description">
            {dealerUser.role === 'dealer_admin' ? 'You can manage users' : 'Staff access'}
          </div>
        </StatsCard>
      </DashboardGrid>

      {dealerUser.role === 'dealer_admin' && (
        <InfoCard>
          <div className="card-header">
            <h2>Admin Tools</h2>
          </div>
          <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
            <ActionButton variant="primary" onClick={() => navigate('/team-users')}>
              <Users />
              Manage Team Users
            </ActionButton>
            <ActionButton onClick={() => navigate('/locations')}>
              <MapPin />
              Manage Locations
            </ActionButton>
            <ActionButton onClick={() => navigate('/services')}>
              <Wrench />
              Manage Services
            </ActionButton>
            <ActionButton onClick={() => navigate(`/dealers/${dealer.id}/embed`)}>
              <Settings />
              Embed Code Settings
            </ActionButton>
          </div>
        </InfoCard>
      )}

      <InfoCard>
        <div className="card-header">
          <h2>Business Information</h2>
        </div>
        <DealerInfo>
          <div className="dealer-name">{dealer.name}</div>
          <div className="info-grid">
            <div className="info-item">
              <Mail />
              <span>{dealer.email}</span>
            </div>
            <div className="info-item">
              <Phone />
              <span>{dealer.phone}</span>
            </div>
            <div className="info-item">
              <MapPin />
              <span>{dealer.address}, {dealer.city}, {dealer.state} {dealer.zipCode}</span>
            </div>
            {dealer.website && (
              <div className="info-item">
                <Globe />
                <a href={dealer.website} target="_blank" rel="noopener noreferrer" style={{ color: '#ed2024' }}>
                  {dealer.website}
                </a>
              </div>
            )}
          </div>
        </DealerInfo>
      </InfoCard>

      <InfoCard>
        <div className="card-header">
          <h2>Your Profile</h2>
        </div>
        <DealerInfo>
          <div className="info-grid">
            <div className="info-item">
              <User />
              <span>{dealerUser.firstName} {dealerUser.lastName}</span>
            </div>
            <div className="info-item">
              <Mail />
              <span>{dealerUser.email}</span>
            </div>
            <div className="info-item">
              <AlertCircle />
              <span>Role: {dealerUser.role === 'dealer_admin' ? 'Administrator' : 'Staff Member'}</span>
            </div>
          </div>
        </DealerInfo>
      </InfoCard>
    </PageContainer>
  );
};

export default DealerDashboard;
